export type InterviewObservabilityContext = {
  traceId?: string | null;
  interviewId?: number | null;
  questionId?: number | null;
  clientAttemptId?: string | null;
};

export type InterviewMetricPayload = InterviewObservabilityContext & {
  stage?: string;
  context?: string;
  provider?: string | null;
  outcome?: string;
  resultClass?: string;
  retryCount?: number;
  requestAttempt?: number;
  recoveryId?: string | null;
  waitMs?: number | null;
  errorClass?: string | null;
  deliveryId?: string | null;
  reasonCode?: string | null;
  checkpoint?: string | null;
  httpStatus?: number | null;
  sessionMode?: string;
  source?: string;
  vadReason?: string | null;
  recordingDurationMs?: number | null;
  peakRms?: number | null;
  averageRms?: number | null;
  speechDurationMs?: number | null;
  strongSpeechDurationMs?: number | null;
  speechRatio?: number | null;
  speechStarted?: boolean | null;
  vadSampleCount?: number | null;
  sampleCount?: number | null;
  minRms?: number | null;
  avgRms?: number | null;
  medianRms?: number | null;
  p90Rms?: number | null;
  maxRms?: number | null;
  startThreshold?: number | null;
  continuationThreshold?: number | null;
  startMarginOverP90?: number | null;
  sufficientSamples?: boolean | null;
};

const SAFE_FIELDS = new Set([
  'trace_id', 'interview_id', 'question_id', 'client_attempt_id', 'stage', 'context',
  'provider', 'outcome', 'result_class', 'retry_count', 'request_attempt',
  'recovery_id', 'wait_ms', 'error_class', 'http_status', 'session_mode', 'source', 'duration_ms',
  'delivery_id', 'reason_code', 'checkpoint',
  'vad_reason', 'recording_duration_ms', 'peak_rms', 'average_rms',
  'speech_duration_ms', 'strong_speech_duration_ms', 'speech_ratio',
  'speech_started', 'vad_sample_count',
  'sample_count', 'min_rms', 'avg_rms', 'median_rms', 'p90_rms', 'max_rms',
  'start_threshold', 'continuation_threshold', 'start_margin_over_p90',
  'sufficient_samples',
]);

const redactString = (value: unknown, maxLength = 160) => {
  const normalized = String(value ?? '')
    .replace(/Bearer\s+[^\s"']+/gi, 'Bearer [REDACTED]')
    .replace(/\b(?:sk|gsk|pk|rk)[-_][A-Za-z0-9._=+/:-]{8,}/gi, '[REDACTED_KEY]')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[REDACTED_EMAIL]')
    .replace(/\b(?:\+?62|0)8\d[\d\s-]{6,}\d\b/g, '[REDACTED_PHONE]')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength)}...` : normalized;
};

export const interviewPerformanceNow = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
};

export const interviewDurationMs = (startedAt: number) => {
  const safeStartedAt = Number(startedAt);
  if (!Number.isFinite(safeStartedAt)) return null;
  return Math.max(0, Math.round((interviewPerformanceNow() - safeStartedAt) * 1000) / 1000);
};

export const createInterviewTraceId = (prefix: 'startup' | 'turn' | 'session' = 'session') => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}-${crypto.randomUUID()}`;
    }
  } catch (_) {}

  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
};

const sanitizeMetricPayload = (payload: Record<string, unknown>) => {
  return Object.entries(payload).reduce<Record<string, string | number | boolean | null>>((safe, [key, value]) => {
    if (!SAFE_FIELDS.has(key) || value === undefined) return safe;
    if (value === null || typeof value === 'boolean') {
      safe[key] = value;
      return safe;
    }
    if (typeof value === 'number') {
      safe[key] = Number.isFinite(value) ? value : null;
      return safe;
    }
    safe[key] = redactString(value);
    return safe;
  }, {});
};

const normalizePayload = (payload: InterviewMetricPayload, durationMs?: number | null) => sanitizeMetricPayload({
  trace_id: payload.traceId,
  interview_id: payload.interviewId,
  question_id: payload.questionId,
  client_attempt_id: payload.clientAttemptId,
  stage: payload.stage,
  context: payload.context,
  provider: payload.provider,
  outcome: payload.outcome,
  result_class: payload.resultClass,
  retry_count: payload.retryCount,
  request_attempt: payload.requestAttempt,
  recovery_id: payload.recoveryId,
  wait_ms: payload.waitMs,
  error_class: payload.errorClass,
  delivery_id: payload.deliveryId,
  reason_code: payload.reasonCode,
  checkpoint: payload.checkpoint,
  http_status: payload.httpStatus,
  session_mode: payload.sessionMode,
  source: payload.source,
  vad_reason: payload.vadReason,
  recording_duration_ms: payload.recordingDurationMs,
  peak_rms: payload.peakRms,
  average_rms: payload.averageRms,
  speech_duration_ms: payload.speechDurationMs,
  strong_speech_duration_ms: payload.strongSpeechDurationMs,
  speech_ratio: payload.speechRatio,
  speech_started: payload.speechStarted,
  vad_sample_count: payload.vadSampleCount,
  sample_count: payload.sampleCount,
  min_rms: payload.minRms,
  avg_rms: payload.avgRms,
  median_rms: payload.medianRms,
  p90_rms: payload.p90Rms,
  max_rms: payload.maxRms,
  start_threshold: payload.startThreshold,
  continuation_threshold: payload.continuationThreshold,
  start_margin_over_p90: payload.startMarginOverP90,
  sufficient_samples: payload.sufficientSamples,
  duration_ms: durationMs,
});

export const trackInterviewMetric = (
  event: string,
  payload: InterviewMetricPayload = {},
  durationMs?: number | null,
) => {
  try {
    const entry = {
      scope: 'interview_frontend',
      event: redactString(event, 100) || 'unknown',
      at: new Date().toISOString(),
      ...normalizePayload(payload, durationMs),
    };
    // Keep production UX quiet; manual P1 profiling runs from the local/dev console.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.info(`[interview_observability] ${JSON.stringify(entry)}`);
    }
    return entry;
  } catch (_) {
    return null;
  }
};

export const trackInterviewDuration = (
  event: string,
  startedAt: number,
  payload: InterviewMetricPayload = {},
) => trackInterviewMetric(event, payload, interviewDurationMs(startedAt));

export const toInterviewObservabilityHeaders = (context?: InterviewObservabilityContext) => {
  const headers: Record<string, string> = {};
  const traceId = redactString(context?.traceId, 160);
  const clientAttemptId = redactString(context?.clientAttemptId, 160);
  const questionId = Number(context?.questionId);

  if (traceId) headers['X-Interview-Trace-Id'] = traceId;
  if (clientAttemptId) headers['X-Interview-Client-Attempt-Id'] = clientAttemptId;
  if (Number.isFinite(questionId) && questionId > 0) headers['X-Interview-Question-Id'] = String(questionId);
  return headers;
};
